import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { defaultOptions, orient } from "@/lib/signs";

const emptyTransform = { x: 0, y: 0, zoom: 1, fit: "cover" };
const emptySide = { file: null, url: null, busy: false, transform: emptyTransform };
const snap = (n) => Math.round(Number(n) * 4) / 4;
const clamp = (n, lo, hi) => Math.min(Math.max(n, lo || 0), hi || Infinity);

/** Everything the designer knows about the sign being built: size, material, options, both images and the live quote. */
export function useSignConfig(product) {
    const [variantId, setVariantId] = useState(null);
    const [presetId, setPresetId] = useState(null);
    const [width, setWidth] = useState("");
    const [height, setHeight] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [options, setOptions] = useState({});
    const [quote, setQuote] = useState(null);
    const [pricing, setPricing] = useState(false);
    const [sides, setSides] = useState({ front: emptySide, back: emptySide });
    const [view, setView] = useState("front");
    const [actual, setActual] = useState(null);
    const slug = product?.slug;
    const timer = useRef(null);

    const freeDims = Boolean(product) && !(product.preset_sizes || []).length && product.min_width_in !== product.max_width_in;

    useEffect(() => {
        if (!product) return;
        setVariantId(product.variants?.[0]?.id || null);
        const p0 = product.preset_sizes?.[0];
        if (p0) {
            setPresetId(p0.id);
            setWidth(p0.width_in);
            setHeight(p0.height_in);
        } else if (product.min_width_in && product.min_width_in === product.max_width_in) {
            setWidth(product.min_width_in);
            setHeight(product.min_height_in);
        } else {
            setWidth("");
            setHeight("");
        }
        setQuantity(product.pricing_model === "per_sheet_bulk" ? 100 : 1);
        setOptions(defaultOptions(product));
        setSides({ front: emptySide, back: emptySide });
        setView("front");
        setActual(null);
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!slug) return;
        clearTimeout(timer.current);
        setPricing(true);
        timer.current = setTimeout(() => {
            api.post("/signs/pricing", {
                slug, variant_id: variantId, preset_id: presetId,
                width: Number(width) || 0, height: Number(height) || 0,
                quantity: Number(quantity) || 0, options,
            }).then(({ data }) => setQuote(data)).catch(() => { }).finally(() => setPricing(false));
        }, 280);
        return () => clearTimeout(timer.current);
    }, [slug, variantId, presetId, width, height, quantity, options]);

    const setOption = (id, value) => setOptions((p) => ({ ...p, [id]: value }));

    // the artboard follows the orientation switch: landscape lies wide, portrait stands tall
    useEffect(() => {
        if (!options.orientation) return;
        const [w, h] = orient(width, height, options.orientation);
        if (w !== width || h !== height) { setWidth(w); setHeight(h); }
    }, [options.orientation]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectPreset = (id) => {
        const p = (product?.preset_sizes || []).find((x) => x.id === id);
        setPresetId(id);
        if (!p) return;
        const [w, h] = orient(p.width_in, p.height_in, options.orientation);
        setWidth(w);
        setHeight(h);
    };

    /** Show the picked file right away, before the upload comes back. */
    const previewImage = (side, url) => setSides((p) => ({ ...p, [side]: { file: null, url, busy: true, transform: emptyTransform } }));

    /** The upload is done. The front image also sets the sign's size to the file's real size. */
    const attachImage = (side, rec, url) => {
        setSides((p) => ({ ...p, [side]: { file: rec, url: url || p[side].url, busy: false, transform: emptyTransform } }));
        if (side !== "front" || !rec?.width_in || !freeDims) return;
        setWidth(clamp(snap(rec.width_in), product.min_width_in, product.max_width_in));
        setHeight(clamp(snap(rec.height_in), product.min_height_in, product.max_height_in));
        setPresetId(null);
        setActual({ w: rec.width_in, h: rec.height_in, px: [rec.width_px, rec.height_px], dpi: rec.dpi, embedded: rec.dpi_embedded });
    };

    const clearImage = (side) => {
        setSides((p) => ({ ...p, [side]: emptySide }));
        if (side === "front") setActual(null);
    };

    const copyFrontToBack = () => setSides((p) => ({ ...p, back: { ...p.front, transform: emptyTransform } }));

    /** Back to the size the file really is. */
    const useActualSize = () => {
        if (!actual) return;
        setWidth(clamp(snap(actual.w), product.min_width_in, product.max_width_in));
        setHeight(clamp(snap(actual.h), product.min_height_in, product.max_height_in));
        setPresetId(null);
    };

    const setTransform = (side, t) => setSides((p) => ({
        ...p, [side]: { ...p[side], transform: typeof t === "function" ? t(p[side].transform) : t },
    }));

    const artwork = [sides.front.file, sides.back.file].filter(Boolean);
    const sizeChanged = Boolean(actual) && (snap(actual.w) !== Number(width) || snap(actual.h) !== Number(height));

    return {
        variantId, setVariantId, presetId, selectPreset, width, setWidth, height, setHeight,
        quantity, setQuantity, options, setOption, quote, pricing, freeDims,
        sides, view, setView, previewImage, attachImage, clearImage, copyFrontToBack, setTransform,
        artwork, actual, sizeChanged, useActualSize,
    };
}

export async function uploadArtwork(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
}
